import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BackendService } from './backend.service';

/** Runtime surface kill-switches (config-repo flags.json → SSM → /agenthome/me).
 *  Hiding/showing a whole hub surface is one config deploy — no UI rebuild. */
export interface HubSurfaces {
  workspace: boolean;
  apps: boolean;
  dashboards: boolean;
  abilities: boolean;
  stats: boolean;
}

/** Caller's authoritative profile from GET /agenthome/me. */
export interface Me {
  is_admin: boolean;
  role: string;
  user_status: string;
  /** Data Hub data-access grants: { RESOURCE_ID: 'own' | 'all' }. */
  data_access: Record<string, string>;
  /** True when the caller sees every Data Hub section (admin/break-glass/dev). */
  data_all: boolean;
  /** null when the backend predates the field — callers then fall back to the
   *  build-time environment flags. */
  surfaces: HubSurfaces | null;
  /** How many apps the caller would see on /apps (granted catalog apps + the
   *  License Extensions built-in). null = unknown (older backend or a server
   *  lookup blip) — callers must fail OPEN and show the surface. */
  apps_available: number | null;
}

/**
 * Resolves the caller's authoritative admin flag from GET /agenthome/me.
 *
 * The backend sources `is_admin` from the USER_REGISTRY role (not the login /
 * Cognito claim), so admin grants/revokes hide or show the Admin Portal button
 * without a re-login. Studio's 'developer' case is handled by the caller using
 * the claim role, since 'developer' is not a USER_REGISTRY role.
 */
@Injectable({ providedIn: 'root' })
export class MeService {
  constructor(
    private readonly http: HttpClient,
    private readonly backend: BackendService,
  ) {}

  /** Caller's authoritative admin flag AND live USER_REGISTRY role from
   *  GET /agenthome/me. `role` is lowercased with '' fallback so callers can
   *  refresh the frozen sessionStorage 'current-role' (which is otherwise
   *  captured once at login and only updated on a full re-login). Errors
   *  propagate so the caller can fall back to the login claim (don't lock
   *  admins out on an outage). */
  getMe(): Observable<Me> {
    const url = this.backend.createAgentsUrl('agenthome/me');
    return this.http
      .get<{ success: boolean; message: string;
             body: { is_admin: boolean; role?: string; user_status?: string;
                     data_access?: Record<string, string>; data_all?: boolean;
                     surfaces?: { workspace?: boolean; apps?: boolean;
                                  dashboards?: boolean; abilities?: boolean;
                                  stats?: boolean };
                     apps_available?: number | null } | null }>(url)
      .pipe(
        map(res => ({
          is_admin: !!res?.body?.is_admin,
          role: (res?.body?.role ?? '').toLowerCase(),
          // Live account status — '' falls back to 'active' so a record predating
          // the field (or an outage) never shows a deactivated state by mistake.
          user_status: (res?.body?.user_status ?? 'active').toLowerCase(),
          // Data Hub data-access summary: {RESOURCE: 'own'|'all'} the caller may
          // see, plus data_all (privileged/break-glass/local-dev → every section).
          // Drives which Data Hub sections render and the own/all behaviour inside.
          data_access: res?.body?.data_access ?? {},
          data_all: !!res?.body?.data_all,
          // Runtime surface kill-switches; null (backend predates the field) →
          // callers keep the build-time environment flag behaviour.
          surfaces: res?.body?.surfaces
            ? {
                workspace: !!res.body.surfaces.workspace,
                apps: !!res.body.surfaces.apps,
                dashboards: !!res.body.surfaces.dashboards,
                abilities: !!res.body.surfaces.abilities,
                stats: !!res.body.surfaces.stats,
              }
            : null,
          // Server-side app count for the Apps nav gate. Anything non-numeric
          // (older backend, server fail-open null) → null = unknown.
          apps_available: typeof res?.body?.apps_available === 'number'
            ? res.body.apps_available : null,
        })),
      );
  }

  /** True iff the caller's USER_REGISTRY role is admin. Errors propagate so the
   *  caller can fall back to the login claim (don't lock admins out on an outage).
   *  Retained for existing callers; delegates to getMe(). */
  isAdmin(): Observable<boolean> {
    return this.getMe().pipe(map(me => me.is_admin));
  }
}
